import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, CircleMarker, Popup, LayersControl, LayerGroup, GeoJSON, useMapEvents } from "react-leaflet";
import L from "leaflet";

// geoBoundaries stores .geojson via Git-LFS — raw.githubusercontent returns pointer files, so we must
// hit media.githubusercontent.com/media/ which serves the actual LFS blob with CORS.
const ADM1_URL = "https://media.githubusercontent.com/media/wmgeolab/geoBoundaries/main/releaseData/gbOpen/GHA/ADM1/geoBoundaries-GHA-ADM1_simplified.geojson";
const ADM2_URL = "https://media.githubusercontent.com/media/wmgeolab/geoBoundaries/main/releaseData/gbOpen/GHA/ADM2/geoBoundaries-GHA-ADM2_simplified.geojson";

function ZoomWatcher({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, RefreshCw, List } from "lucide-react";

const CATEGORY_LABEL = {
  violence: "Violence", ballot_snatching: "Ballot snatching", over_voting: "Over-voting",
  obstruction: "Obstruction", equipment_malfunction: "Equipment malfunction", bribery: "Vote-buying",
  intimidation: "Intimidation", misconduct: "Officer misconduct", media_denied: "Media denied",
  underage_voting: "Under-age voting", impersonation: "Impersonation", other: "Other",
};
const SEV_COLOR = { low: "#0EA5E9", medium: "#F59E0B", high: "#EF4444", critical: "#A855F7" };
const SEV_RADIUS = { low: 6, medium: 8, high: 10, critical: 13 };

// Fix leaflet default icon path (not strictly needed since we use CircleMarker, but safe)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function IncidentMap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", severity: "" });
  const [showRegions, setShowRegions] = useState(true);
  const [regions, setRegions] = useState([]);
  const [polygons, setPolygons] = useState(null);
  const [adm2, setAdm2] = useState(null);
  const [zoom, setZoom] = useState(6);
  const [layerError, setLayerError] = useState(null);
  const adm2LoadingRef = React.useRef(false);
  const [constituencyWinners, setConstituencyWinners] = useState([]);

  useEffect(() => {
    api.get("/public/geo/constituencies").then(({ data }) => setConstituencyWinners(data || [])).catch(() => setConstituencyWinners([]));
    // Live repaint: subscribe to the active election's WS and refresh on constituency.winner_changed
    let ws;
    (async () => {
      try {
        const { data: els } = await api.get("/elections");
        const active = els.find(e => e.status === "active");
        if (!active) return;
        const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/collation/${active.id}?token=${encodeURIComponent(localStorage.getItem("ec_token") || "")}`;
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          let m = null; try { m = JSON.parse(ev.data); } catch { return; }
          if (m.type === "constituency.winner_changed" || m.type === "result.approve") {
            api.get("/public/geo/constituencies").then(({ data }) => setConstituencyWinners(data || [])).catch(() => {});
          }
        };
      } catch {}
    })();
    return () => { try { ws && ws.close(); } catch {} };
  }, []);

  useEffect(() => {
    fetch(ADM1_URL)
      .then(async r => { if (!r.ok) throw new Error(`ADM1 ${r.status}`); return r.json(); })
      .then(setPolygons)
      .catch(err => { setPolygons(null); setLayerError(`Region boundaries unavailable: ${err.message}`); });
  }, []);
  // Lazy-load ADM2 the first time the user zooms past the region threshold. Guard with a ref to prevent
  // React StrictMode double-mount + repeat zoomend from firing multiple fetches.
  useEffect(() => {
    if (zoom >= 8 && !adm2 && !adm2LoadingRef.current) {
      adm2LoadingRef.current = true;
      fetch(ADM2_URL)
        .then(async r => { if (!r.ok) throw new Error(`ADM2 ${r.status}`); return r.json(); })
        .then(data => { setAdm2(data); adm2LoadingRef.current = false; })
        .catch(err => { setAdm2(null); adm2LoadingRef.current = false; setLayerError(`Constituency boundaries unavailable: ${err.message}`); });
    }
  }, [zoom, adm2]);

  const load = async () => {
    setLoading(true);
    try {
      const [inc, geo] = await Promise.all([
        api.get("/incidents"),
        api.get("/public/geo/regions").catch(() => ({ data: [] })),
      ]);
      setItems(inc.data);
      setRegions(geo.data || []);
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const geo = useMemo(() => items.filter(i => typeof i.lat === "number" && typeof i.lng === "number"), [items]);
  const filtered = useMemo(() => geo.filter(i =>
    (!filter.category || i.category === filter.category) &&
    (!filter.severity || i.severity === filter.severity)
  ), [geo, filter]);

  const center = filtered.length
    ? [filtered.reduce((s,i)=>s+i.lat,0)/filtered.length, filtered.reduce((s,i)=>s+i.lng,0)/filtered.length]
    : [7.9465, -1.0232]; // Ghana centroid fallback

  const bySev = useMemo(() => {
    const g = { low: 0, medium: 0, high: 0, critical: 0 };
    for (const i of filtered) g[i.severity] = (g[i.severity] || 0) + 1;
    return g;
  }, [filtered]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Reporting Engine</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="incident-map-title">Incident Map</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Every geolocated incident, live. Marker size and colour show severity — click a marker for the report.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/incidents" data-testid="back-to-list" className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2">
            <List size={13}/> List view
          </Link>
          <button onClick={load} data-testid="map-refresh" className="p-2 border border-white/20 hover:bg-white/10">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          </button>
        </div>
      </div>

      {/* Filters + legend */}
      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
        <select value={filter.category} onChange={e=>setFilter(f=>({...f, category:e.target.value}))} data-testid="map-filter-cat"
          className="bg-transparent border border-white/20 px-3 py-1.5 outline-none">
          <option value="" className="bg-black">Any category</option>
          {Object.entries(CATEGORY_LABEL).map(([k,v]) => <option key={k} value={k} className="bg-black">{v}</option>)}
        </select>
        <select value={filter.severity} onChange={e=>setFilter(f=>({...f, severity:e.target.value}))} data-testid="map-filter-sev"
          className="bg-transparent border border-white/20 px-3 py-1.5 outline-none">
          <option value="" className="bg-black">Any severity</option>
          <option value="low" className="bg-black">Low</option>
          <option value="medium" className="bg-black">Medium</option>
          <option value="high" className="bg-black">High</option>
          <option value="critical" className="bg-black">Critical</option>
        </select>
        <div className="flex-1"/>
        <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 cursor-pointer" data-testid="toggle-regions">
          <input type="checkbox" checked={showRegions} onChange={e=>setShowRegions(e.target.checked)} className="accent-emerald-500 w-4 h-4"/>
          {zoom >= 8 ? "Constituencies" : "Region winners"}
        </label>
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="zoom-level">z={zoom}</span>
        {layerError && <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400" data-testid="layer-error" title={layerError}>⚠ boundaries offline</span>}
        <div className="flex items-center gap-3 text-[11px]">
          {Object.entries(SEV_COLOR).map(([k,c]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className="rounded-full" style={{ background: c, width: SEV_RADIUS[k]*1.4, height: SEV_RADIUS[k]*1.4 }}/>
              <span className="uppercase tracking-wider text-zinc-400">{k}</span>
              <span className="text-white tabular">{bySev[k]||0}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="mt-4 border border-white/10 overflow-hidden" style={{ height: "70vh", minHeight: 520 }} data-testid="incident-map">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-zinc-500 text-sm gap-2">
            <Loader2 size={16} className="animate-spin"/> Loading map…
          </div>
        ) : filtered.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center flex-col gap-2 text-zinc-500 text-sm text-center px-6">
            <MapPin size={22}/>
            No geolocated incidents to plot yet.
            <div className="text-xs">Reporters can capture location when filing — those pins land here in real time.</div>
          </div>
        ) : (
          <MapContainer center={center} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%", background: "#0a0a0a" }}>
            <ZoomWatcher onZoom={setZoom} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Dark">
                <TileLayer attribution='&copy; OpenStreetMap contributors'
                  className="leaflet-tile-dark"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Streets">
                <TileLayer attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>
            </LayersControl>
            {showRegions && zoom >= 8 && adm2 && (
              <GeoJSON
                key={`adm2-${adm2?.features?.length || 0}-${constituencyWinners.length}`}
                data={adm2}
                style={(feature) => {
                  const name = (feature?.properties?.shapeName || "").trim().toLowerCase();
                  const w = constituencyWinners.find(x => (x.name || "").trim().toLowerCase() === name);
                  const color = w?.color || "#5AC8FA";
                  const share = w?.share || 0;
                  return { color, weight: 0.8, fillColor: color, fillOpacity: w ? (0.15 + share * 0.5) : 0.05 };
                }}
                onEachFeature={(f, layer) => {
                  const name = f?.properties?.shapeName || "Constituency";
                  const w = constituencyWinners.find(x => (x.name || "").trim().toLowerCase() === (name || "").trim().toLowerCase());
                  const html = w
                    ? `<div style="min-width:220px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:0.2em;color:#71717A">Constituency · ${name}</div><div style="font-weight:700;font-size:14px;margin-top:4px;color:${w.color}">${w.winner || "—"} · ${w.party_code || ""}</div><div style="font-size:12px;margin-top:4px">${(w.share*100).toFixed(1)}% share · ${w.total_votes.toLocaleString()} votes</div></div>`
                    : `<div style="min-width:180px"><div style="font-weight:600">${name}</div><div style="font-size:11px;color:#71717A;margin-top:4px">No approved results yet</div></div>`;
                  layer.bindPopup(html);
                  layer.bindTooltip(name, { sticky: true, direction: "top" });
                }}
              />
            )}
            {showRegions && zoom < 8 && polygons && (
              <GeoJSON
                key={`gj-${regions.length}`}
                data={polygons}
                style={(feature) => {
                  const name = (feature?.properties?.shapeName || "").trim().toLowerCase();
                  const r = regions.find(x => x.name.trim().toLowerCase() === name);
                  const color = r?.color || "#3f3f46";
                  const share = r?.share || 0;
                  return { color, weight: 1, fillColor: color, fillOpacity: 0.15 + share * 0.4 };
                }}
                onEachFeature={(feature, layer) => {
                  const name = feature?.properties?.shapeName || "Region";
                  const r = regions.find(x => x.name.trim().toLowerCase() === (name).trim().toLowerCase());
                  const html = r
                    ? `<div style="min-width:220px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:0.2em;color:#71717A">${name}</div><div style="font-weight:700;font-size:15px;margin-top:4px;color:${r.color}">${r.winner || "—"} · ${r.party_code || ""}</div><div style="font-size:12px;margin-top:4px">${(r.share*100).toFixed(1)}% share · ${r.total_votes.toLocaleString()} votes · ${r.reported_pu} PU</div></div>`
                    : `<div style="min-width:180px"><div style="font-weight:600">${name}</div><div style="font-size:11px;color:#71717A;margin-top:4px">No approved results yet</div></div>`;
                  layer.bindPopup(html);
                }}
              />
            )}
            {showRegions && !polygons && regions.map(r => {
              const radius = 22 + Math.round((r.share || 0) * 40);
              return (
                <CircleMarker key={r.region_id} center={[r.lat, r.lng]} radius={radius}
                  pathOptions={{ color: r.color, fillColor: r.color, fillOpacity: 0.18, weight: 2, dashArray: "4 4" }}>
                  <Popup>
                    <div style={{ minWidth: 220 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#71717A" }}>Region · {r.name}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4, color: r.color }}>{r.winner || "—"} <span style={{ color: "#71717A" }}>· {r.party_code}</span></div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>{(r.share * 100).toFixed(1)}% share · {r.total_votes.toLocaleString()} votes · {r.reported_pu} PU</div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
            {filtered.map(i => {
              const color = SEV_COLOR[i.severity] || "#71717A";
              const radius = SEV_RADIUS[i.severity] || 6;
              return (
                <CircleMarker key={i.id} center={[i.lat, i.lng]} radius={radius}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.55, weight: 2 }}>
                  <Popup>
                    <div style={{ minWidth: 200 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#71717A" }}>
                        {CATEGORY_LABEL[i.category] || i.category} · {i.severity}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14, marginTop: 4 }}>{i.title}</div>
                      <div style={{ fontSize: 11, color: "#71717A", marginTop: 6 }}>
                        {(i.reporter_email||"").split("@")[0]} · {(i.created_at||"").replace("T"," ").slice(0,16)}
                      </div>
                      <div style={{ fontSize: 11, color: "#71717A", marginTop: 4 }}>
                        Status: <span style={{ color: "#111" }}>{i.status}</span>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}
      </div>

      <div className="mt-3 text-[11px] text-zinc-500 flex items-center justify-between flex-wrap gap-2">
        <div>{filtered.length} of {items.length} incident{items.length===1?"":"s"} plotted · {items.length - geo.length} missing coordinates</div>
        <div>Tip: reporters must tap <span className="text-zinc-300">"Capture location"</span> when filing for a pin to appear.</div>
      </div>
    </div>
  );
}
